import { computed, inject, reactive, ref } from 'vue';

import FormInputComponent from '@/components/_shared/_partials/form-input/form-input.vue';
import regionCountryService from '@/services/region.country.service';
import FormSelectRegionCountryComponent
  from '@/components/_shared/_partials/form-select-region-country/form-select-region-country.vue';
import FormSelectStateCountryComponent
  from '@/components/_shared/_partials/form-select-state-country/form-select-state-country.vue';

import chargifyConfig from './chargify-config';

async function initialize() {
  const scripts = [
    'https://js.chargify.com/latest/chargify.js',
    'https://www.paypalobjects.com/api/checkout.js',
    'https://js.braintreegateway.com/web/3.55.0/js/client.min.js',
    'https://js.braintreegateway.com/web/3.55.0/js/paypal-checkout.min.js',
  ];
  await scripts.forEach((script) => {
    const tag = document.createElement('script');
    tag.src = script;
    document.head.appendChild(tag);
  });
}

export default {
  name: 'PaymentCreateComponent',
  components: { FormInputComponent, FormSelectRegionCountryComponent, regionCountryService, FormSelectStateCountryComponent },
  props: {
    profiles: {
      type: Array,
      required: false
    },
    canCreate: {
      type: Boolean,
      default: false
    }
  },
  async mounted() {
    await initialize();
    await this.loadConfig();
    await this.initializeChargify();
    await this.initializeBraintree();
  },
  setup() {
    const config = inject('config');

    const subscriptionProvider = inject('subscriptionProvider');

    const loadConfig = async function() {
      const checkoutConfig = await subscriptionProvider().loadConfig(config);
    };

    const state = reactive({
      showNotification: ref(false),
      notification: ref(null),
      payment_type: ref('authorizenet'),
      email: null,
      first_name: null,
      last_name: null,
      billing_address: null,
      billing_address_2: null,
      billing_city: null,
      billing_state: null,
      billing_zip: null,
      billing_country: null,
      chargify_token: null,
      update: async (data) => {
        const { key, value } = data;
        state[key] = value;
      },
      canSubmit: computed(() => {
        const { first_name, last_name, billing_address, billing_city, billing_state, billing_zip, billing_country } = state;
        return first_name && last_name && billing_address && billing_city && billing_state && billing_zip && billing_country;
      }),
      countries: regionCountryService.getCountries(),
      states: computed(() => {
        if (state.billing_country) {
          return regionCountryService.getRegions(state.billing_country);
        }
        return [];
      }),
      stateKey: ref(1),
    });

    const missingFields = () => {
      // if (!state.chargify_token) return 'Please fill out the Payment Details!'
      let message;
      if (!state.first_name) {
        message = 'Please fill out the Billing Details First Name field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.last_name) {
        message = 'Please fill out the Billing Details Last Name field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.billing_address) {
        message = 'Please fill out the Billing Street Address field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.billing_city) {
        message = 'Please fill out the Billing Details City field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.billing_country) {
        message = 'Please use the dropdown to select the Billing Details Country field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.billing_state) {
        message = 'Please use the dropdown to select the Billing Details State field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }
      if (!state.billing_zip) {
        message = 'Please fill out the Billing Details Postal Code field!';
        subscriptionProvider().setValidProfile(message);
        return message;
      }

      subscriptionProvider().setValidProfile(true);
      return false;
    };

    const initializeChargify = async function() {
      this.state.showNotification = true;
      this.state.notification = 'Initializing...';

      const form = document.querySelector('#chargify-form');
      if (!form) {
        return;
      }

      const checkoutConfig = await subscriptionProvider().loadConfig(config);

      chargifyConfig.publicKey = checkoutConfig.data.data.checkout.authorizenet.publicKey;
      chargifyConfig.serverHost = checkoutConfig.data.data.checkout.authorizenet.host;
      chargifyConfig.securityToken = checkoutConfig.data.data.checkout.authorizenet.token;

      const chargify = new Chargify();
      chargify.load(chargifyConfig);

      this.state.showNotification = false;
      this.state.notification = null;

      form.addEventListener('create', (event) => {
        event.preventDefault();
        if (this.missingFields()) {
          return false;
        }
        this.state.showNotification = true;
        this.state.notification = 'Validating Payment Details...';

        this.state.isValidating = true;

        if (this.state.payment_type === 'braintree_blue') {
          this.state.showNotification = false;
          this.state.notification = null;
          return this.create();
        }
        return chargify.token(
          form,
          (responseToken) => {
            this.state.chargify_token = responseToken;
            this.state.payment_type = 'authorizenet';

            this.state.showNotification = false;
            this.state.notification = null;
            return this.create();
          },
          (error) => {
            console.log('error', error);
            if (error.message) {
              document.body.scrollTop = form.scrollIntoView();
            }
            this.state.showNotification = true;
            this.state.notification = error.message || error.errors;
            return false;
          },
        );
      });
    };

    const initializeBraintree = async function() {
      const self = this;
      const checkoutConfig = await subscriptionProvider().loadConfig(config);
      const { token, environment } = checkoutConfig.data.data.checkout.braintree_blue;

      braintree.client.create(
        {
          authorization: token,
        },
        (clientErr, clientInstance) => {
          if (clientErr) {
            console.error('Error creating client:', clientErr);
            return;
          }

          braintree.paypalCheckout.create(
            {
              client: clientInstance,
            },
            (paypalCheckoutErr, paypalCheckoutInstance) => {
              if (paypalCheckoutErr) {
                console.error(
                  'Error creating PayPal Checkout:',
                  paypalCheckoutErr,
                );
                return;
              }

              paypal.Button.render(
                {
                  env: environment,
                  enableShippingAddress: true,

                  style: {
                    layout: 'horizontal',
                    tagline: false,
                  },

                  payment() {
                    return paypalCheckoutInstance.createPayment({
                      flow: 'vault',
                    });
                  },

                  onAuthorize(authData) {
                    return paypalCheckoutInstance.tokenizePayment(authData, (
                      err,
                      payload,
                    ) => {
                      console.log('payload', payload);
                      self.state.chargify_token = payload.nonce;
                      self.state.payment_type = 'braintree_blue';
                      self.state.first_name = payload.details.firstName;
                      self.state.last_name = payload.details.lastName;
                      self.state.email = payload.details.email;
                      self.state.billing_country = regionCountryService.getCountryByShortCode(
                        payload.details.countryCode);
                      self.state.validateError = null;
                      self.state.isValidating = false;
                      self.state.stateKey++;
                    });
                  },
                },
                '#paypal-button',
              );
            },
          );
        },
      );
    };

    const create = async function() {
      const {
        email,
        first_name,
        last_name,
        billing_address,
        billing_address_2,
        billing_city,
        billing_zip,
        chargify_token,
        payment_type,
      } = state;

      const billing_country = state.billing_country.countryShortCode;
      const billing_state = state.billing_state.shortCode;

      this.$emit('create', {
        email,
        first_name,
        last_name,
        billing_address,
        billing_address_2,
        billing_city,
        billing_state,
        billing_zip,
        billing_country,
        chargify_token,
        payment_type
      });

      subscriptionProvider().setCreatingProfile(false);
    };

    const clearNotification = async () => state.showNotification = false;

    const lookupIPAddress = async () => {
      let response = await regionCountryService.lookupIP();
      if (response && response.country_code) {
        let country = regionCountryService.getCountryByShortCode(response.country_code);
        if (country && country.name) {
          state.billing_country = country;
          if (response.region_code) {
            let region = regionCountryService.getRegionByName(country.name,
              response.region_code);
            if (region) {
              state.billing_state = region;
            }
          }
          state.stateKey++;
        }
      }

    };

    lookupIPAddress();

    return {
      state,
      loadConfig,
      initializeChargify,
      initializeBraintree,
      create,
      clearNotification,
      missingFields
    };
  },
  methods: {
    login: async function() {
      const { username, password } = this.state;
      this.$emit('login', { username, password });
    },
    submit: async function() {
      const event = new Event('create');
      document.querySelector('#chargify-form').dispatchEvent(event);
    },
    countrySelect(data) {
      this.state.billing_country = data;
      this.state.billing_state = null;
      this.state.stateKey++;
    },
    regionSelect(data) {
      this.state.billing_state = data;
    },
  }
};
